import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { z } from 'zod/v3'

import type { ShopPostCategory } from '@/lib/api/shop-cms-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { ErrorText, TextField } from '@/components/retail/form-bits'
import {
    setShopServerErrors,
    shopFieldErrors,
} from '@/components/retail/shop-errors'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useDeleteShopPostCategory,
    useSaveShopPostCategory,
    useShopPostCategories,
} from '@/hooks/use-shop-posts-query'
import { m } from '@/lib/paraglide/messages'

// Bán lẻ › Chuyên mục tin tức (web /tin-tuc?chuyen-muc=). Chuyên mục còn bài
// (kể cả bản nháp) thì không xoá được.
export const Route = createFileRoute(
    '/_app/_authed/_sidebar/retail/post-categories',
)({
    component: PostCategoriesPage,
})

interface CategoryForm {
    label: string
    slug: string
    sort_order: string
}

const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/

export const createPostCategorySchema = () =>
    z.object({
        label: z
            .string()
            .trim()
            .min(1, m.cmsPostCategory_error_label())
            .max(120, m.error_shop_field_too_long({ max: '120' })),
        slug: z
            .string()
            .trim()
            .max(80, m.error_shop_field_too_long({ max: '80' }))
            .refine((s) => s === '' || SLUG_RE.test(s), m.cmsPost_error_slug()),
        sort_order: z
            .string()
            .trim()
            .refine(
                (s) => s === '' || (/^\d+$/.test(s) && Number(s) <= 10000),
                m.cmsPostCategory_error_sort(),
            ),
    })

const EMPTY: CategoryForm = { label: '', slug: '', sort_order: '' }

export function PostCategoriesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('shop_content.write')
    const query = useShopPostCategories()
    const rows = query.data?.data ?? []
    const save = useSaveShopPostCategory()
    const remove = useDeleteShopPostCategory()
    const [editing, setEditing] = useState<ShopPostCategory | null>(null)
    const [listError, setListError] = useState<string | null>(null)
    const form = useForm<CategoryForm>({
        resolver: zodResolver(createPostCategorySchema()),
        defaultValues: EMPTY,
    })

    const edit = (c: ShopPostCategory | null) => {
        setEditing(c)
        form.reset(
            c
                ? {
                      label: c.label,
                      slug: c.slug,
                      sort_order: String(c.sort_order),
                  }
                : EMPTY,
        )
    }

    const submit = form.handleSubmit(async (v) => {
        try {
            await save.mutateAsync({
                id: editing?.id,
                input: {
                    label: v.label.trim(),
                    slug: v.slug.trim(),
                    sort_order: Number(v.sort_order || 0),
                },
            })
            toast.success(m.cmsPostCategory_saved())
            edit(null)
        } catch (err) {
            setShopServerErrors(
                form.setError,
                err,
                m.cmsPostCategory_save_failed(),
            )
        }
    })

    const del = async (c: ShopPostCategory) => {
        if (
            !window.confirm(
                m.cmsPostCategory_delete_confirm({ label: c.label }),
            )
        )
            return
        setListError(null)
        try {
            await remove.mutateAsync(c.id)
            toast.success(m.cmsPostCategory_deleted())
        } catch (err) {
            setListError(
                shopFieldErrors(err, m.cmsPostCategory_delete_failed()).form ??
                    null,
            )
        }
    }

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.cmsPostCategory_title()}
                subtitle={m.cmsPostCategory_subtitle()}
            />
            {canWrite && (
                <Card className="max-w-3xl">
                    <CardContent className="pt-6">
                        <FormProvider {...form}>
                            <form
                                onSubmit={submit}
                                noValidate
                                className="space-y-3"
                            >
                                <div className="grid gap-3 sm:grid-cols-3">
                                    <TextField
                                        name="label"
                                        label={m.cmsPostCategory_label()}
                                    />
                                    <TextField
                                        name="slug"
                                        label={m.cmsPost_slug()}
                                        hint={m.cmsPost_slug_hint()}
                                    />
                                    <TextField
                                        name="sort_order"
                                        label={m.cmsPostCategory_sort()}
                                    />
                                </div>
                                <ErrorText
                                    text={
                                        form.formState.errors.root?.server
                                            ?.message
                                    }
                                />
                                <div className="flex gap-2">
                                    <Button
                                        type="submit"
                                        disabled={save.isPending}
                                    >
                                        {editing
                                            ? m.common_save()
                                            : m.cmsPostCategory_add()}
                                    </Button>
                                    {editing && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => edit(null)}
                                        >
                                            {m.common_cancel()}
                                        </Button>
                                    )}
                                </div>
                            </form>
                        </FormProvider>
                    </CardContent>
                </Card>
            )}
            <ErrorText text={listError} />
            <div className="max-w-3xl overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.cmsPostCategory_label()}</TableHead>
                            <TableHead>{m.cmsPost_slug()}</TableHead>
                            <TableHead className="text-right">
                                {m.cmsPostCategory_posts()}
                            </TableHead>
                            {canWrite && <TableHead />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {query.isError && (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="text-destructive"
                                    role="alert"
                                >
                                    {m.cmsPostCategory_load_failed()}
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((c) => (
                            <TableRow key={c.id}>
                                <TableCell className="font-medium">
                                    {c.label}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {c.slug}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {m.cmsPostCategory_counts({
                                        published: String(c.post_count),
                                        draft: String(c.draft_count),
                                    })}
                                </TableCell>
                                {canWrite && (
                                    <TableCell className="space-x-2 text-right">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => edit(c)}
                                        >
                                            {m.common_edit()}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => void del(c)}
                                            aria-label={m.cmsPostCategory_delete_aria(
                                                { label: c.label },
                                            )}
                                        >
                                            {m.common_delete()}
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
